//React and Packages
import * as React from "react";
import { observer, inject } from "mobx-react";
import * as moment from "moment";

//Custom Components
import EditableCard, { EditableDateGroup, EditableTextareaGroup } from "../common/EditableCard";
import LoadingWidget from "../common/LoadingWidget";
import AttachmentLogPanel from "../common/AttachmentLogPanel";

//Store
import { HorseForSaleStore } from "../../stores/HorseForSaleStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

//Model
import { HorseForSale, HorseForSaleAttachments } from "../../model/HorseForSale";

const EMPTY_DATE = "";

//Props = PropsStore + PropsComponents
export interface PropsStores {
    horseForSaleStore?: HorseForSaleStore;
    configurationStore?: ConfigurationStore;
}
export interface PropsComponent {}
type Props = PropsStores & PropsComponent;

//State = StateComponent + State Editable Field
export interface StateComponent {
    isEditable: boolean;
    wasValidated: boolean;
}
export interface StateFields {
    inspectionDate: moment.Moment | "";
    inspectionNotes: string;
}
type State = StateComponent & StateFields;

@inject("horseForSaleStore")
@inject("configurationStore")
@observer
export default class Inspection extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            wasValidated: false,

            inspectionDate: EMPTY_DATE,
            inspectionNotes: ""
        };
    }

    //Default Dattivo Form Data Manipulation
    editFieds = () => {
        this.setState(this.copyHorseForSaleToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        this.props
            .horseForSaleStore!.updateFieldsByObject(this.buildFieldsToUpdate())
            .then(() => this.setState({ isEditable: false }))
            .catch(err => alert(err));
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    //Basic Form React
    handleOnChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    submit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        this.saveEditableFields();
    };

    //Component Functions
    getValidDates = (currentDate: any) => {
        return currentDate.isAfter(moment());
    };

    handleChangeInspectionDate = (date: any): void => {
        if (date.format) {
            this.setState({ inspectionDate: date as moment.Moment });
        }
    };

    render() {
        //Not Null Stores
        const horseForSaleStore = this.props.horseForSaleStore;
        if (!horseForSaleStore) {
            return <div />;
        }

        const horseForSale = horseForSaleStore.horseForSale;
        if (!horseForSale) {
            return <div />;
        }

        const pageTitle = "Inspection";

        return (
            <>
                <EditableCard
                    title={pageTitle}
                    editable={this.state.isEditable}
                    onEdit={this.editFieds}
                    onCancel={this.cancelEditableFields}
                    onSave={this.saveEditableFields}
                >
                    <form
                        id="owners-form"
                        className={"needs-validation " + (this.state.wasValidated ? " was-validated" : "")}
                        onSubmit={e => this.submit(e)}
                        noValidate={true}
                    >
                        <div className="form-row">
                            <EditableDateGroup
                                label="Inpection Date"
                                invalidFeedBack="Please provide a date."
                                name="inspectionDate"
                                className="col-sm-12 col-md-6 col-lg-4"
                                inputClassName="not-required"
                                dateFormat="YYYY-MM-DD"
                                timeFormat="h:mm a"
                                isValidDate={this.getValidDates}
                                editable={this.state.isEditable}
                                editableValue={this.state.inspectionDate}
                                nonEditableValue={horseForSale.inspectionDate ? moment(horseForSale.inspectionDate) : EMPTY_DATE}
                                onChange={this.handleChangeInspectionDate}
                            />
                            {this.state.isEditable && (
                                <div className="col-lg-4">
                                    <button
                                        type="button"
                                        onClick={() => this.setState({ inspectionDate: EMPTY_DATE })}
                                        className="btn btn-secondary"
                                        style={{ marginTop: "32px" }}
                                    >
                                        Clear Date
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <EditableTextareaGroup
                                label="Notes"
                                rows={5}
                                name="inspectionNotes"
                                className="col-md-12"
                                inputClassName="not-required"
                                editable={this.state.isEditable}
                                editableValue={this.state.inspectionNotes}
                                nonEditableValue={horseForSale.inspectionNotes || ""}
                                onChange={this.handleOnChange}
                            />
                        </div>
                    </form>
                </EditableCard>
                <AttachmentLogPanel
                    title={pageTitle}
                    attachmentLog={horseForSale.attachments.inspectionFiles}
                    onAddAttachment={horseForSaleStore.addAttachment.bind(horseForSaleStore)}
                    onDeleteAttachment={horseForSaleStore.deleteAttachment.bind(horseForSaleStore)}
                    isEditable={true}
                />
            </>
        );
    }

    private copyHorseForSaleToState(otherFields: Partial<State> = {}): Partial<State> {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const updatedState: Partial<State> = Object.assign(
            {
                inspectionDate: horseForSale.inspectionDate ? moment(horseForSale.inspectionDate) : EMPTY_DATE,
                inspectionNotes: horseForSale.inspectionNotes || ""
            },
            otherFields
        );

        return updatedState;
    }

    private buildFieldsToUpdate(): { [field: string]: string | number | null } {
        const horseForSale = this.props.horseForSaleStore!.horseForSale;
        if (!horseForSale) {
            return {};
        }

        const obj: { [field: string]: string | number | null } = {};

        const inspectionTime =
            this.state.inspectionDate && this.state.inspectionDate.format ? (this.state.inspectionDate as moment.Moment).toDate().getTime() : null;
        if (horseForSale.inspectionDate) {
            if (horseForSale.inspectionDate !== inspectionTime) {
                obj.inspectionDate = inspectionTime;
            }
        } else if (this.state.inspectionDate !== EMPTY_DATE) {
            obj.inspectionDate = inspectionTime;
        }

        if (horseForSale.inspectionNotes !== this.state.inspectionNotes) {
            obj.inspectionNotes = this.state.inspectionNotes;
        }

        return obj;
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/Inspection.tsx